import {
  RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH,
  RETURNS_AUTOMATIONS_SETTINGS_PATH,
  RETURNS_WAREHOUSE_SETTINGS_PATH,
} from "./settings-paths";

export enum ReturnsSettingsTab {
  COVERAGE = "coverage",
  POLICIES = "policies",
  PORTAL = "portal",
  OPERATIONS = "operations",
  LABELS = "labels",
  LOCATIONS = "locations",
  SHOPPER_NOTIFICATIONS = "shopper-notifications",
  WAREHOUSE = "warehouse",
}

export const RETURNS_SETTINGS_TAB_QUERY_PARAM = "tab";

export function isReturnsSettingsTab(
  value: string | null,
): value is ReturnsSettingsTab {
  return Object.values<string>(ReturnsSettingsTab).includes(value ?? "");
}

/**
 * Where switching back lands from each tab. A tab replaces more than one old
 * page; this is the one a merchant would name if asked where the settings used
 * to live. Shopper notifications replaced Marketing automations, not a settings
 * page, so it falls back to Coverage like an unknown tab.
 */
const LEGACY_SETTINGS_PATH_BY_TAB: Record<
  Exclude<ReturnsSettingsTab, ReturnsSettingsTab.SHOPPER_NOTIFICATIONS>,
  string
> = {
  [ReturnsSettingsTab.COVERAGE]: "coverage",
  [ReturnsSettingsTab.POLICIES]: "customer-portal",
  [ReturnsSettingsTab.PORTAL]: "customer-portal",
  [ReturnsSettingsTab.OPERATIONS]: "exchanges",
  [ReturnsSettingsTab.LABELS]: "shipping",
  [ReturnsSettingsTab.LOCATIONS]: "shipping",
  [ReturnsSettingsTab.WAREHOUSE]: "warehouse",
};

export function legacySettingsPathForTab(tab: string | null): string {
  if (
    !isReturnsSettingsTab(tab) ||
    tab === ReturnsSettingsTab.SHOPPER_NOTIFICATIONS
  ) {
    return LEGACY_SETTINGS_PATH_BY_TAB[ReturnsSettingsTab.COVERAGE];
  }
  return LEGACY_SETTINGS_PATH_BY_TAB[tab];
}

/** The old page for a new-page link's query, keeping its highlight params. */
export function legacySettingsHrefForSearch(search: string): string {
  const params = new URLSearchParams(search);
  const path = legacySettingsPathForTab(
    params.get(RETURNS_SETTINGS_TAB_QUERY_PARAM),
  );
  params.delete(RETURNS_SETTINGS_TAB_QUERY_PARAM);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** Old pages the new page replaced, with the tab switching forward lands on. */
export const RETURNS_SETTINGS_TAB_BY_LEGACY_PATH: Readonly<
  Record<string, ReturnsSettingsTab>
> = {
  [RETURNS_AUTOMATIONS_SETTINGS_PATH]: ReturnsSettingsTab.OPERATIONS,
  [RETURNS_AUTOMATIONS_PROCESSING_SETTINGS_PATH]: ReturnsSettingsTab.OPERATIONS,
  coverage: ReturnsSettingsTab.COVERAGE,
  "customer-portal": ReturnsSettingsTab.PORTAL,
  exchanges: ReturnsSettingsTab.OPERATIONS,
  processing: ReturnsSettingsTab.OPERATIONS,
  "returns-ui": ReturnsSettingsTab.WAREHOUSE,
  shipping: ReturnsSettingsTab.LABELS,
  [RETURNS_WAREHOUSE_SETTINGS_PATH]: ReturnsSettingsTab.WAREHOUSE,
};
